import { useEffect, useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAppDispatch } from "store";
import { setGlobalComponent } from "store/global-components";
import { useAppTranslation } from "locale/useAppTranslation";
import { vehicleModelPageRouteName } from "../list/index.page";
import { parseFormattedNumber } from "_helper/formatters";
import {
  useVehicleModelServiceV1GetVehicleModelQuery,
  useVehicleModelServiceV1UpdateVehicleModelMutation,
  useVehicleModelServiceV1ListTollClassesQuery,
  useVehicleModelServiceV1GetFuelTypeDropdownQuery,
  useVehicleModelServiceV1GetVehicleTypeDropdownQuery,
  useVehicleModelServiceV1GetFreezerModelDropdownQuery,
  VehicleModelServiceV1UpdateVehicleModelBody,
} from "_services/modules/vehicle-model";
import { toast } from "react-toastify";
import { errorHandler } from "_services/errorHandler";

export interface EditVehicleModelFormValues {
  vehicleModel: string;
  vehicleCategory: string;
  vehicleType: string;
  vehicleTypeId: string;
  vehicleTypeIsFreezer: boolean;
  freezerModel: string;
  freezerModelId: string;
  tyreCount: string;
  tyreSize: string;
  axleCount: string;
  tollClass: string;
  tollClassId: string;
  fuelType: string;
  fuelTypeId: string;
  fuelCapacity: string;
  status: string;
}

const useEditVehicleModelForm = (id: string | undefined) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useAppTranslation("vehicleModel");

  const schema = useMemo(() => yup.object({
    vehicleModel: yup.string().required().max(100),
    vehicleCategory: yup.string().oneOf(["truck", "chassis"]).required(),
    vehicleType: yup.string().required(),
    vehicleTypeId: yup.string().required(),
    vehicleTypeIsFreezer: yup.boolean().default(false),
    freezerModel: yup.string().when(["vehicleCategory", "vehicleTypeIsFreezer"], {
      is: (category: string, isFreezer: boolean) =>
        category === "truck" && isFreezer === true,
      then: (s) => s.required(),
      otherwise: (s) => s.notRequired().default(""),
    }),
    freezerModelId: yup.string().when(["vehicleCategory", "vehicleTypeIsFreezer"], {
      is: (category: string, isFreezer: boolean) =>
        category === "truck" && isFreezer === true,
      then: (s) => s.required(),
      otherwise: (s) => s.notRequired().default(""),
    }),
    tyreCount: yup
      .number()
      .typeError(t("error-type-number"))
      .required()
      .min(1),
    tyreSize: yup.string().notRequired(),
    axleCount: yup
      .number()
      .typeError(t("error-type-number"))
      .required()
      .min(1),
    tollClass: yup.string().required(),
    tollClassId: yup.string().required(),
    fuelType: yup.string().when("vehicleCategory", {
      is: "truck",
      then: (s) => s.required(),
      otherwise: (s) => s.notRequired().default(""),
    }),
    fuelTypeId: yup.string().when("vehicleCategory", {
      is: "truck",
      then: (s) => s.required(),
      otherwise: (s) => s.notRequired().default(""),
    }),
    fuelCapacity: yup
      .number()
      .transform((value, originalValue) => {
        if (originalValue === "") return null;
        if (typeof originalValue === "string") return parseFormattedNumber(originalValue);
        return value;
      })
      .typeError(t("error-type-number"))
      .nullable()
      .min(0)
      .notRequired(),
    status: yup.string().required(),
  }).required(), [t]);

  const { data: detail, isLoading } = useVehicleModelServiceV1GetVehicleModelQuery(
    { id: id || "" },
    { skip: !id }
  );
  const [updateVehicleModel] = useVehicleModelServiceV1UpdateVehicleModelMutation();
  const { data: tollClassesData } = useVehicleModelServiceV1ListTollClassesQuery();
  const { data: fuelTypesData } = useVehicleModelServiceV1GetFuelTypeDropdownQuery();
  const { data: vehicleTypesData } = useVehicleModelServiceV1GetVehicleTypeDropdownQuery();
  const { data: freezerModelsData } = useVehicleModelServiceV1GetFreezerModelDropdownQuery();

  const tollClassOptions = useMemo(() => {
    if (!tollClassesData?.toll_classes) return [];
    return tollClassesData.toll_classes.map((tc) => ({
      label: tc.name || "",
      value: tc.name || "",
      id: tc.id || "",
    }));
  }, [tollClassesData]);

  const fuelTypeOptions = useMemo(() => {
    if (!fuelTypesData?.fuel_types) return [];
    return fuelTypesData.fuel_types.map((ft) => ({
      label: ft.fuel_type_name || "",
      value: ft.fuel_type_name || "",
      id: ft.id || "",
    }));
  }, [fuelTypesData]);

  const freezerModelOptions = useMemo(() => {
    if (!freezerModelsData?.freezer_models) return [];
    return freezerModelsData.freezer_models.map((fm) => ({
      label: fm.name || "",
      value: fm.name || "",
      id: fm.id || "",
    }));
  }, [freezerModelsData]);

  const {
    handleSubmit,
    formState: { errors },
    register,
    control,
    setError,
    watch,
    reset,
    setValue,
  } = useForm<EditVehicleModelFormValues>({
    mode: "onBlur",
    resolver: yupResolver(schema) as any,
    defaultValues: {
      vehicleModel: "",
      vehicleCategory: "truck",
      vehicleType: "",
      vehicleTypeId: "",
      vehicleTypeIsFreezer: false,
      freezerModel: "",
      freezerModelId: "",
      tyreCount: "",
      tyreSize: "",
      axleCount: "",
      tollClass: "",
      tollClassId: "",
      fuelType: "",
      fuelTypeId: "",
      fuelCapacity: "",
      status: "active",
    },
  });

  useEffect(() => {
    if (detail) {
      const vehicleTypeRecord = vehicleTypesData?.vehicle_types?.find(
        (vt) => vt.id === detail.vehicle_type_id,
      );
      reset({
        vehicleModel: detail.vehicle_model || "",
        vehicleCategory: vehicleTypeRecord?.category?.toLowerCase() === "chassis" ? "chassis" : "truck",
        vehicleType: detail.vehicle_type_name || "",
        vehicleTypeId: detail.vehicle_type_id || "",
        vehicleTypeIsFreezer: vehicleTypeRecord?.is_freezer ?? false,
        freezerModel: detail.freezer_model_name || "",
        freezerModelId: detail.freezer_model_id || "",
        tyreCount: String(detail.tyre_count || ""),
        tyreSize: detail.tyre_size || "",
        axleCount: String(detail.axle_count || ""),
        tollClass: detail.toll_class_name || "",
        tollClassId: detail.toll_class_id || "",
        fuelType: detail.fuel_type_name || "",
        fuelTypeId: detail.fuel_type_id || "",
        fuelCapacity: detail.fuel_capacity !== undefined && detail.fuel_capacity !== null ? String(detail.fuel_capacity) : "",
        status: detail.status || "active",
      });
    }
  }, [detail, vehicleTypesData, fuelTypesData, reset]);

  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [pendingData, setPendingData] =
    useState<EditVehicleModelFormValues | null>(null);

  const watchedCategory = watch("vehicleCategory");
  const watchedVehicleType = watch("vehicleType");
  const watchedFreezerModel = watch("freezerModel");
  const watchedFuelType = watch("fuelType");

  const isTruck = watchedCategory === "truck";
  const selectedVehicleTypeData = vehicleTypesData?.vehicle_types?.find(
    (vt) => vt.name === watchedVehicleType,
  );
  const isFreezerTruck = isTruck && (selectedVehicleTypeData?.is_freezer ?? false);

  // Sync vehicleTypeIsFreezer flag when vehicle type selection changes
  useEffect(() => {
    const match = vehicleTypesData?.vehicle_types?.find(
      (vt) => vt.name === watchedVehicleType,
    );
    const isFreezer = match?.is_freezer ?? false;
    setValue("vehicleTypeIsFreezer", isFreezer);
    if (!isFreezer) {
      setValue("freezerModel", "");
      setValue("freezerModelId", "");
    }
  }, [watchedVehicleType, vehicleTypesData, setValue]);

  // Sync freezerModelId whenever the selected freezer model name changes
  useEffect(() => {
    if (!watchedFreezerModel) {
      setValue("freezerModelId", "");
      return;
    }
    const match = freezerModelOptions.find((opt) => opt.value === watchedFreezerModel);
    setValue("freezerModelId", match?.id ?? "");
  }, [watchedFreezerModel, freezerModelOptions, setValue]);

  // Sync fuelTypeId whenever the selected fuel type name changes
  useEffect(() => {
    if (!watchedFuelType) {
      setValue("fuelTypeId", "");
      return;
    }
    const match = fuelTypeOptions.find((opt) => opt.value === watchedFuelType);
    setValue("fuelTypeId", match?.id ?? "");
  }, [watchedFuelType, fuelTypeOptions, setValue]);

  useEffect(() => {
    dispatch(
      setGlobalComponent({
        title: t("page-title-edit"),
        hasBackButton: true,
      })
    );
  }, [dispatch, t]);

  const onSubmit = (data: EditVehicleModelFormValues) => {
    setPendingData(data);
    setShowSaveModal(true);
  };

  const handleConfirmSave = async () => {
    if (!pendingData || !id) return;
    try {
      const updateBody: VehicleModelServiceV1UpdateVehicleModelBody = {
        vehicle_model: pendingData.vehicleModel,
        vehicle_type_id: pendingData.vehicleTypeId,
        tyre_count: Number(pendingData.tyreCount),
        axle_count: Number(pendingData.axleCount),
        toll_class_id: pendingData.tollClassId,
        fuel_type_id: pendingData.fuelTypeId,
        status: pendingData.status,
      };
      if (pendingData.tyreSize) updateBody.tyre_size = pendingData.tyreSize;
      if (pendingData.fuelCapacity) updateBody.fuel_capacity = Number(pendingData.fuelCapacity);
      await updateVehicleModel({
        id,
        vehicleModelServiceV1UpdateVehicleModelBody: updateBody,
      }).unwrap();
      toast.success(t("toast-update-success"));
      setShowSaveModal(false);
      navigate(-1);
    } catch (error: any) {
      if (error.status === 409) {
        setError("vehicleModel", {
          type: "validate",
          message: t("error-duplicate-exists"),
        });
        setShowSaveModal(false);
      } else {
        errorHandler(error);
        setShowSaveModal(false);
      }
    }
  };

  const handleConfirmCancel = () => {
    setShowCancelModal(false);
    navigate(-1);
  };

  return {
    handleSubmit,
    errors,
    register,
    control,
    setError,
    showSaveModal,
    setShowSaveModal,
    showCancelModal,
    setShowCancelModal,
    onSubmit,
    handleConfirmSave,
    handleConfirmCancel,
    t,
    isTruck,
    isFreezerTruck,
    watchedCategory,
    watchedVehicleType,
    tollClassOptions,
    fuelTypeOptions,
    freezerModelOptions,
    setValue,
    isLoading,
  };
};

export default useEditVehicleModelForm;
